import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Container,
  FormGroup,
  Input,
  Label,
  Spinner,
} from "reactstrap";

import AdatisLogo from "../../../Components/Common/AdatisLogo";
import {
  getPublicClubJoin,
  postPublicClubJoin,
  PublicClubJoinData,
  PublicClubPlan,
} from "../../../services/publicClubService";

const formatMoneyFromCents = (cents: number, locale: string): string => {
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "BRL",
    }).format(cents / 100);
  } catch {
    return `R$ ${(cents / 100).toFixed(2)}`;
  }
};

const PublicClubJoinPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { token = "" } = useParams<{ token: string }>();

  const [data, setData] = useState<PublicClubJoinData | null>(null);
  const [selectedPlanId, setSelectedPlanId] = useState<number | "">("");
  const [suggestedPlanId, setSuggestedPlanId] = useState<number | null>(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [expired, setExpired] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [paymentLink, setPaymentLink] = useState<string | null>(null);

  document.title = t("public_club.pageTitle");

  const load = useCallback(async (): Promise<void> => {
    if (!token) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setNotFound(false);
    setExpired(false);

    try {
      const response = await getPublicClubJoin(token);
      const payload = response.data ?? null;
      setData(payload);
      if (payload?.suggested_plan_id) {
        setSuggestedPlanId(payload.suggested_plan_id);
        setSelectedPlanId(payload.suggested_plan_id);
      } else if (payload?.plans?.length === 1) {
        setSuggestedPlanId(null);
        setSelectedPlanId(payload.plans[0].id);
      } else {
        setSuggestedPlanId(null);
      }
    } catch (err) {
      const apiError = err as { code?: string };

      if (apiError.code === "CLUB_TOKEN_EXPIRED") {
        setExpired(true);
        return;
      }

      if (apiError.code === "CLUB_TOKEN_INVALID" || apiError.code === "NOT_FOUND") {
        setNotFound(true);
        return;
      }

      setError(t("public_club.errors.load"));
    } finally {
      setLoading(false);
    }
  }, [token, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const selectedPlan: PublicClubPlan | undefined = useMemo(
    () => data?.plans?.find((plan) => plan.id === selectedPlanId),
    [data?.plans, selectedPlanId]
  );

  const handleJoin = async (): Promise<void> => {
    if (!token || !selectedPlanId || !acceptedTerms) {
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccessMessage(null);
    setPaymentLink(null);

    try {
      const response = await postPublicClubJoin(token, {
        client_subscription_plan_id: Number(selectedPlanId),
        accepted_terms: true,
      });
      const result = response.data;
      setSuccessMessage(t("public_club.success"));
      setPaymentLink(result?.payment_link_url ?? null);

      if (result?.payment_link_url) {
        window.location.assign(result.payment_link_url);
      }
    } catch (err) {
      const apiError = err as {
        code?: string;
        data?: { error?: { message?: string; code?: string } };
        message?: string;
      };
      const message =
        apiError.data?.error?.message ||
        apiError.message ||
        t("public_club.errors.join");
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const alreadyMember = Boolean(data?.current_membership);

  return (
    <div className="auth-page-wrapper auth-bg-cover py-5 d-flex justify-content-center align-items-center min-vh-100">
      <Container style={{ maxWidth: 560 }}>
        <div className="bg-white rounded-3 shadow-lg p-4 p-md-5">
          <div className="text-center mb-4">
            <AdatisLogo height={28} />
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : expired ? (
            <Alert color="warning" className="mb-0">
              {t("public_club.expired")}
            </Alert>
          ) : notFound ? (
            <Alert color="danger" className="mb-0">
              {t("public_club.notFound")}
            </Alert>
          ) : (
            <>
              <p className="text-muted text-uppercase small mb-1">
                {data?.studio_name}
              </p>
              <h1 className="h4 mb-2">{t("public_club.title")}</h1>
              {data?.client_first_name ? (
                <p className="text-muted mb-3">
                  {t("public_club.hello", { name: data.client_first_name })}
                </p>
              ) : (
                <p className="text-muted mb-3">{t("public_club.subtitle")}</p>
              )}

              {alreadyMember ? (
                <Alert color="info" className="mb-3">
                  {t("public_club.alreadyMember", {
                    plan: data?.current_membership?.plan_name ?? "",
                  })}
                </Alert>
              ) : null}

              {!data?.plans?.length ? (
                <Alert color="info" className="mb-0">
                  {t("public_club.emptyPlans")}
                </Alert>
              ) : (
                <div className="d-flex flex-column gap-2 mb-3">
                  {data.plans.map((plan) => {
                    const selected = selectedPlanId === plan.id;
                    return (
                      <button
                        key={plan.id}
                        type="button"
                        className={`text-start border rounded p-3 bg-white ${
                          selected ? "border-primary" : ""
                        }`}
                        onClick={() => setSelectedPlanId(plan.id)}
                        disabled={alreadyMember || submitting}
                      >
                        <div className="d-flex justify-content-between gap-2 align-items-start">
                          <div className="fw-semibold">{plan.name}</div>
                          {suggestedPlanId === plan.id ? (
                            <span className="badge bg-primary-subtle text-primary">
                              {t("public_club.suggested")}
                            </span>
                          ) : null}
                        </div>
                        {plan.description ? (
                          <div className="text-muted small">{plan.description}</div>
                        ) : null}
                        <div className="mt-1">
                          {formatMoneyFromCents(plan.price, i18n.language)}
                          <span className="text-muted small ms-1">
                            / {t("public_club.perMonth")}
                          </span>
                        </div>
                        {plan.monthly_visit_limit != null ? (
                          <div className="text-muted small">
                            {t("public_club.visitLimit", {
                              limit: plan.monthly_visit_limit,
                            })}
                          </div>
                        ) : (
                          <div className="text-muted small">
                            {t("public_club.visitUnlimited")}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {error ? (
                <Alert color="danger" className="mb-3">
                  {error}
                </Alert>
              ) : null}

              {successMessage ? (
                <Alert color="success" className="mb-3">
                  {successMessage}
                  {paymentLink ? (
                    <div className="mt-2">
                      <a href={paymentLink} rel="noreferrer">
                        {t("public_club.openPayment")}
                      </a>
                    </div>
                  ) : (
                    <div className="mt-2 small">{t("public_club.awaitPayment")}</div>
                  )}
                </Alert>
              ) : null}

              {!alreadyMember && data?.plans?.length ? (
                <>
                  <FormGroup check className="mb-3">
                    <Input
                      id="club-terms"
                      type="checkbox"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                    />
                    <Label check htmlFor="club-terms" className="ms-1">
                      {t("public_club.terms")}
                    </Label>
                  </FormGroup>
                  <Button
                    color="primary"
                    className="w-100"
                    disabled={!selectedPlan || !acceptedTerms || submitting}
                    onClick={() => {
                      void handleJoin();
                    }}
                  >
                    {submitting ? (
                      <Spinner size="sm" />
                    ) : selectedPlan ? (
                      t("public_club.joinWithPrice", {
                        price: formatMoneyFromCents(
                          selectedPlan.price,
                          i18n.language
                        ),
                      })
                    ) : (
                      t("public_club.join")
                    )}
                  </Button>
                  <p className="text-muted small mt-3 mb-0">
                    {t("public_club.lgpdHint")}
                  </p>
                </>
              ) : null}
            </>
          )}
        </div>
      </Container>
    </div>
  );
};

export default PublicClubJoinPage;
